import React, { useEffect, useState, useRef } from 'react';
import { getUserActivitiesByDateRange, Activity, bulkUpdateActivitySuspiciousStatus } from '../services/activitiesService';
import { formatDistance, formatTime, formatDate, formatSpeed } from '../utils/formatters';
import { FaFlag, FaTimes, FaCheck } from 'react-icons/fa';
import { isAdmin } from '../services/authService';
import './UserActivitiesDialog.css';

interface UserActivitiesDialogProps {
  userId: number;
  userName: string;
  startDateLocal: string;
  endDate: string;
  isOpen: boolean;
  onClose: () => void;
}

const UserActivitiesDialog: React.FC<UserActivitiesDialogProps> = ({
  userId,
  userName,
  startDateLocal,
  endDate,
  isOpen,
  onClose
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedActivities, setSelectedActivities] = useState<number[]>([]);
  const [isAdminUser, setIsAdminUser] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsAdminUser(isAdmin());
  }, []);

  // Tách hàm fetchActivities ra để có thể gọi lại khi cần
  const fetchActivities = async () => {
    if (!isOpen) return;
    
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      const data = await getUserActivitiesByDateRange(userId, startDateLocal, endDate);
      setActivities(data);
      // Reset selections when loading new data
      setSelectedActivities([]);
    } catch (err: any) {
      console.error('Lỗi khi lấy danh sách hoạt động:', err);
      setError(err.message || 'Không thể lấy danh sách hoạt động');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [userId, startDateLocal, endDate, isOpen]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const formatDateWithTime = (dateArray: number[]) => {
    if (!dateArray || dateArray.length < 5) return '--';
    const [year, month, day, hour, minute] = dateArray;
    
    // Tạo chuỗi định dạng trực tiếp để tránh lỗi bị trừ 1 ngày khi tạo Date
    return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  const handleSelectActivity = (activityId: number) => {
    setSelectedActivities(prev => {
      if (prev.includes(activityId)) {
        return prev.filter(id => id !== activityId);
      } else {
        return [...prev, activityId];
      }
    });
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedActivities(activities.map(activity => activity.id));
    } else {
      setSelectedActivities([]);
    }
  };

  const handleUpdateSuspiciousStatus = async (status: 0 | 1) => {
    if (selectedActivities.length === 0) {
      setError('Vui lòng chọn ít nhất một hoạt động để cập nhật');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      const result = await bulkUpdateActivitySuspiciousStatus(selectedActivities, status);
      
      // Hiển thị thông báo thành công
      setSuccessMessage(result.message);
      
      // Làm mới dữ liệu từ server thay vì chỉ cập nhật state local
      await fetchActivities();
    } catch (err: any) {
      console.error('Lỗi khi cập nhật trạng thái hoạt động:', err);
      setError(err.message || 'Không thể cập nhật trạng thái hoạt động');
    } finally {
      setActionLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="user-activities-dialog-overlay" onClick={handleOverlayClick}>
      <div className="user-activities-dialog" ref={dialogRef}>
        <div className="user-activities-dialog-header">
          <h2>Hoạt động của {userName}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="user-activities-dialog-content">
          {loading ? (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Đang tải dữ liệu...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
            </div>
          ) : successMessage ? (
            <div className="success-message">
              <p>{successMessage}</p>
              <div className="activities-table-container">
                <table className="activities-table">
                  <thead>
                    <tr>
                      {isAdminUser && <th style={{ width: '40px' }}>Chọn</th>}
                      <th>Tên hoạt động</th>
                      <th>Loại</th>
                      <th>Quãng đường</th>
                      <th>Thời gian</th>
                      <th>Tốc độ</th>
                      <th>Ngày</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((activity) => (
                      <tr 
                        key={activity.id}
                        className={activity.suspiciousActivity === 1 ? 'suspicious-row' : ''}
                      >
                        {isAdminUser && (
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedActivities.includes(activity.id)}
                              onChange={() => handleSelectActivity(activity.id)}
                              disabled={actionLoading}
                            />
                          </td>
                        )}
                        <td>
                          {activity.name} {activity.suspiciousActivity === 1 && <FaFlag className="suspicious-flag" color="red" title="Hoạt động đáng nghi ngờ" />}
                        </td>
                        <td>{activity.sportType}</td>
                        <td>{formatDistance(activity.distance)}</td>
                        <td>{formatTime(activity.movingTime)}</td>
                        <td>{formatSpeed(activity.averageSpeed)}</td>
                        <td>{formatDateWithTime(activity.startDateLocal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activities.length === 0 ? (
            <div className="empty-state">
              <p>Không có hoạt động nào trong khoảng thời gian này</p>
            </div>
          ) : (
            <>
              {isAdminUser && (
                <div className="admin-actions">
                  <div className="admin-actions-heading">
                    <span>Quản lý hoạt động đáng nghi ngờ</span>
                  </div>
                  <div className="admin-buttons">
                    <button
                      className="flag-button mark-suspicious"
                      onClick={() => handleUpdateSuspiciousStatus(1)}
                      disabled={actionLoading || selectedActivities.length === 0}
                    >
                      <FaFlag /> Đánh dấu đáng nghi ngờ
                    </button>
                    <button
                      className="flag-button remove-suspicious"
                      onClick={() => handleUpdateSuspiciousStatus(0)}
                      disabled={actionLoading || selectedActivities.length === 0}
                    >
                      <FaTimes /> Gỡ đánh dấu
                    </button>
                    <div className="select-all-container">
                      <input
                        type="checkbox"
                        id="select-all"
                        checked={selectedActivities.length === activities.length}
                        onChange={handleSelectAll}
                        disabled={actionLoading}
                      />
                      <label htmlFor="select-all">Chọn tất cả</label>
                    </div>
                  </div>
                  <div className="admin-info">
                    {actionLoading ? (
                      <div className="action-loading">
                        <div className="spinner-small"></div>
                        <span>Đang xử lý...</span>
                      </div>
                    ) : (
                      <span>Đã chọn {selectedActivities.length}/{activities.length} hoạt động</span>
                    )}
                  </div>
                </div>
              )}
              
              <div className="activities-table-container">
                <table className="activities-table">
                  <thead>
                    <tr>
                      {isAdminUser && <th style={{ width: '40px' }}>Chọn</th>}
                      <th>Tên hoạt động</th>
                      <th>Loại</th>
                      <th>Quãng đường</th>
                      <th>Thời gian</th>
                      <th>Tốc độ</th>
                      <th>Ngày</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((activity) => (
                      <tr 
                        key={activity.id}
                        className={activity.suspiciousActivity === 1 ? 'suspicious-row' : ''}
                      >
                        {isAdminUser && (
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedActivities.includes(activity.id)}
                              onChange={() => handleSelectActivity(activity.id)}
                              disabled={actionLoading}
                            />
                          </td>
                        )}
                        <td>
                          {activity.name} {activity.suspiciousActivity === 1 && <FaFlag className="suspicious-flag" color="red" title="Hoạt động đáng nghi ngờ" />}
                        </td>
                        <td>{activity.sportType}</td>
                        <td>{formatDistance(activity.distance)}</td>
                        <td>{formatTime(activity.movingTime)}</td>
                        <td>{formatSpeed(activity.averageSpeed)}</td>
                        <td>{formatDateWithTime(activity.startDateLocal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        
        <div className="user-activities-dialog-footer">
          <button onClick={onClose} className="cancel-button">Đóng</button>
        </div>
      </div>
    </div>
  );
};

export default UserActivitiesDialog; 